import React, { useState } from 'react';
import {
  Box, Typography, Paper, Alert, ToggleButton, ToggleButtonGroup, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

import { reportingCostAPI } from '../services/reportingCostApi';
import { MonthSelect, WithMonths, Kpi, KpiGrid, Legend, CostBar, usd, pct, useLoad, downloadCsv, NET, SP, EDP, BAD } from './ReportingCost';

const Loading = () => <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

function TaggingGauge({ taggedPct }) {
  const untagged = 100 - taggedPct;
  const color = untagged > 20 ? BAD : untagged > 10 ? '#ed6c02' : SP;
  return (
    <Box role="img" aria-label={`${taggedPct.toFixed(1)}% of spend tagged`}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="caption">Tagged {taggedPct.toFixed(1)}%</Typography><Typography variant="caption" sx={{ color }}>{untagged.toFixed(1)}% untagged</Typography></Box>
      <Box sx={{ display: 'flex', height: 10, borderRadius: 0.5, overflow: 'hidden', bgcolor: 'action.hover' }}>
        <Box sx={{ width: `${taggedPct}%`, bgcolor: SP }} /><Box sx={{ width: `${untagged}%`, bgcolor: color, opacity: 0.7 }} />
      </Box>
    </Box>
  );
}

function UntaggedPanel({ month }) {
  const [view, setView] = useState('service');
  const { data, error } = useLoad(async () => {
    const [s, r] = await Promise.all([reportingCostAPI.untaggedServices(month), reportingCostAPI.untaggedRegions(month)]);
    return { services: s.data, regions: r.data };
  }, [month]);
  const list = data ? (view === 'service' ? data.services : data.regions) : [];
  const max = Math.max(1, ...list.map((r) => r.cost_gross));
  return (
    <Paper sx={{ p: 2, mt: 2, borderLeft: 4, borderColor: BAD }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant="subtitle1">Untagged spend breakdown</Typography>
        <ToggleButtonGroup exclusive size="small" value={view} onChange={(e, v) => v && setView(v)} aria-label="Untagged breakdown">
          <ToggleButton value="service">By service</ToggleButton><ToggleButton value="region">By region</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {!data && !error && <Loading />}
      {data && (
        <TableContainer sx={{ maxHeight: 420 }}>
          <Table size="small" stickyHeader aria-label="Untagged spend">
            <TableHead><TableRow><TableCell>{view === 'service' ? 'Service' : 'Region'}</TableCell>{view === 'service' && <TableCell>Region</TableCell>}<TableCell sx={{ width: '25%' }} /><TableCell align="right">Gross</TableCell><TableCell align="right">Net</TableCell></TableRow></TableHead>
            <TableBody>
              {list.map((r, i) => (
                <TableRow key={i} hover>
                  <TableCell>{view === 'service' ? r.aws_service : r.region}</TableCell>
                  {view === 'service' && <TableCell>{r.region}</TableCell>}
                  <TableCell><Box sx={{ height: 6, borderRadius: 0.5, bgcolor: BAD, opacity: 0.7, width: `${(r.cost_gross / max) * 100}%` }} /></TableCell>
                  <TableCell align="right" sx={{ color: BAD }}>{usd(r.cost_gross)}</TableCell><TableCell align="right">{usd(r.cost_net)}</TableCell>
                </TableRow>
              ))}
              {list.length === 0 && <TableRow><TableCell colSpan={5}>Nothing untagged.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>These resources are not attributed to any customer. Add the Client tag to resolve.</Typography>
    </Paper>
  );
}

export default function ReportingFullCost() {
  return <WithMonths>{(options) => <FullCostPage options={options} />}</WithMonths>;
}

function FullCostPage({ options }) {
  const [month, setMonth] = useState(options[0].value);
  const [exportError, setExportError] = useState('');
  const { data, error } = useLoad(async () => {
    const [s, a] = await Promise.all([reportingCostAPI.accountSummary(month), reportingCostAPI.accountAll(month)]);
    return { summary: s.data, all: a.data };
  }, [month]);
  const s = data?.summary;
  const untaggedPct = Number(s?.untagged_pct || 0);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Full AWS Cost</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The whole AWS account for a month: every customer plus the spend that carries no Client tag, with tagging coverage and where the untagged spend goes.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <MonthSelect value={month} onChange={setMonth} options={options} />
        <Button size="small" startIcon={<DownloadIcon />} onClick={() => downloadCsv(month, setExportError)} sx={{ ml: { sm: 'auto' } }}>Export CSV</Button>
      </Box>
      {(error || exportError) && <Alert severity="error" sx={{ mb: 2 }}>{error || exportError}</Alert>}
      {!data && !error && <Loading />}
      {data && (
        <>
          {untaggedPct > 10 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Tagging compliance: {untaggedPct.toFixed(1)}% of spend is unattributed. {usd(s.untagged_gross)} cannot be attributed to a customer this month; resources missing the Client tag are broken down below.
            </Alert>
          )}
          <KpiGrid>
            <Kpi label="Total account cost" value={usd(s.total_net)} sub="net after all discounts" color={NET} />
            <Kpi label="Gross (list) cost" value={usd(s.total_gross)} sub="before SP + EDP" />
            <Kpi label="Total savings" value={usd(s.total_saving)} sub={`${pct(s.saving_pct)} saved`} color={SP} />
            <Kpi label="Untagged spend" value={usd(s.untagged_gross)} sub={`${pct(s.untagged_pct)} of total`} color={untaggedPct > 10 ? BAD : undefined} />
          </KpiGrid>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
            <Paper sx={{ p: 2 }}><Typography variant="subtitle2" gutterBottom>Tagging coverage</Typography><TaggingGauge taggedPct={100 - untaggedPct} /></Paper>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Discount summary</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box><Typography variant="caption" color="text.secondary">Savings Plans</Typography><Typography variant="h6" sx={{ color: SP }}>{usd(s.tagged_saving)}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">EDP</Typography><Typography variant="h6" sx={{ color: EDP }}>{usd(s.tagged_edp)}</Typography></Box>
              </Box>
            </Paper>
          </Box>
          <Paper>
            <Box sx={{ p: 2, pb: 1 }}><Typography variant="subtitle1">All costs: customers and untagged</Typography><Legend items={[['Net (tagged)', NET], ['Net (untagged)', BAD], ['Savings', SP]]} /></Box>
            <TableContainer sx={{ maxHeight: 560 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow><TableCell>Customer</TableCell><TableCell sx={{ width: '20%' }} /><TableCell align="right">Gross</TableCell><TableCell align="right">Net</TableCell><TableCell align="right">SP saved</TableCell><TableCell align="right">EDP</TableCell><TableCell align="right">Saving</TableCell></TableRow></TableHead>
                <TableBody>
                  {data.all.map((c) => (
                    <TableRow key={c.customer_code} hover sx={c.is_tagged ? undefined : { bgcolor: 'rgba(211,47,47,0.06)' }}>
                      <TableCell><Typography variant="body2" sx={{ fontWeight: 500, color: c.is_tagged ? undefined : BAD }}>{c.is_tagged ? c.customer_code : 'UNTAGGED'}</Typography><Typography variant="caption" color="text.secondary">{c.cust_name}</Typography></TableCell>
                      <TableCell><CostBar gross={c.cost_gross} net={c.cost_net} untagged={!c.is_tagged} /></TableCell>
                      <TableCell align="right">{usd(c.cost_gross)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: c.is_tagged ? undefined : BAD }}>{usd(c.cost_net)}</TableCell>
                      <TableCell align="right">{usd(c.cost_sp_saving)}</TableCell><TableCell align="right">{usd(c.cost_edp)}</TableCell><TableCell align="right">{pct(c.saving_pct)}</TableCell>
                    </TableRow>
                  ))}
                  {data.all.length === 0 && <TableRow><TableCell colSpan={7}>No cost recorded for this month.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          {data.all.some((c) => !c.is_tagged) && <UntaggedPanel month={month} />}
        </>
      )}
    </Box>
  );
}
